<template>
	<div class="content-page content-page--beta-features-history">
		<div class="content-page__heading">
			<div class="content-page__breadcrumb-title">
				<Breadcrumbs :items="urlNames" />
				<h2 class="content-page__title">
					Beta Feature History
					<span class="content-page__title--detail" v-if="pageTitle"
						>- {{ pageTitle }}</span
					>
				</h2>
			</div>
		</div>

		<div class="results-toolbar results-toolbar--variant-three">
			<HistoryFilters
				v-model="filters"
				:defaultFilters="defaultFilters"
				:userOptions="users || []"
				:placeholder="placeholderHistory"
				:datePickerMinDate="new Date('01/01/2025')"
				:disableDaysBeforeStartDate="true"
				:ddUserWidth="'185px'"
				:datePickerWidth="'185px'"
				:searchWidth="searchWidth"
				:startDate="new Date('01/01/2025')"
			/>
			<!--DEV NOTE: New BetaFeature Logs will be available starting 1/1/2025.-->

			<div class="results-toolbar__counter" v-if="resultCounter">
				<!--DEV NOTE: Component is pending development's implemention.-->
				<ResultCounter :counter="resultCounter" />
			</div>
		</div>

		<!--DEV NOTE: Component is pending development's implemention.-->
		<Table
			:sourceObject="tableSource"
			@change="updatedPagination"
			@sort="updateDirection"
			v-if="hasResults"
			:isArrowUp="true"
			:sortOptions="handleSort"
			class="table-container--beta-features-history"
		>
			<template #col-group>
				<colgroup>
					<!-- Change Date-->
					<col class="table__col table__col--date" />

					<!-- Feature -->
					<col
						v-if="hideFeatureColumn"
						class="table__col table__col--feature"
					/>

					<!-- Action -->
					<col class="table__col table__col--action" />

					<!-- Changed By -->
					<col class="table__col table__col--changed-by" />
				</colgroup>
			</template>
			<template #body-timestamp="{ body }">
				<div class="beta-history-data">
					{{ fullDateString(body.timestamp) }}

					<div class="beta-history-data__text-emphasis">
						<strong>{{
							differenceDateString(body.timestamp)
						}}</strong>
					</div>
				</div>
			</template>
			<template #body-actions="{ body, index }">
				<div
					class="beta-history-data__text-action"
					ref="actionWidthRef"
				>
					<template>
						<div
							class="beta-history-data__action-description"
							v-html="body.actionDescription"
						></div>
						<div
							class="beta-history-data__action-detail-button"
							v-if="
								!body.goLive &&
									body.actionDescription.length > 0
							"
						>
							&nbsp;
							<a
								class="link"
								v-on:click.prevent="
									() => displayModalClick(index)
								"
							>
								view details
							</a>
						</div>
					</template>
				</div>
			</template>
		</Table>

		<FeaturesCustomersModal
			v-if="dataDisplay"
			:logs="featureLog"
			@close="closeFeaturesCustomerModal"
		/>
		<Spinner :processor="{ loading: loading }" />

		<NoResults v-if="!hasResults && !loading" />
	</div>
</template>

<script>
import Breadcrumbs from '@/components/shells/breadcrumbs/breadcrumbs';
import FeaturesCustomersModal from '@/sections/beta-features/history/features-customers-modal.vue';
import NoResults from '@/site-components/no-results/no-results';
import ResultCounter from '@/site-components/result-counter/result-counter';
import Spinner from '@/components/visual/spinner/spinner';
import Table from '@/components/shells/table/table';
import headerOptions from './header-options';
import { mapActions, mapGetters, mapState } from 'vuex';
import CustomerOptionsMixins from '@/mixins/customers/customer-options';
import HistoryFilters from '@/pages/global-history-screen-filters';
import FeaturesLogs from '@/sections/beta-features/mixins/features-logs.js';
// libraries
import { formatDate } from '@/utils/datetime';
import { sub } from 'date-fns';

//constants
import {
	BETA_FEATURES,
	MODULE_NAME,
	SET_BETA_FEATURES_ALL,
	BETA_FEATURES_ALL
} from '../beta-features-mutation-types';
import {
	MODULE_NAME as OPTION_MODULE_NAME,
	LOGS,
	SET_LOGS,
	SET_USERS_LOGS
} from '@/sections/logs/logs-mutation-type';
import _ from 'lodash';

import {
	removeObjectEmptyValues,
	removePropertiesWithSameValues
} from '@/utils/object';
import { parseDateToUTC } from '@/utils/date/parseDate';

export default {
	name: 'BetaFeaturesLogs',
	mixins: [CustomerOptionsMixins, FeaturesLogs],
	components: {
		HistoryFilters,
		Breadcrumbs,
		FeaturesCustomersModal,
		ResultCounter,
		NoResults,
		Spinner,
		Table
	},
	props: {
		orderBy: String,
		orderDirection: String,
		search: String,
		minDatetime: String,
		maxDatetime: String,
		changedByUserId: String,
		SourceApplication: String,
		changedEntityId: [String, Number],
		recordStart: Number,
		maxCount: Number
	},
	data() {
		return {
			actionClientWidth: 0,
			filters: {},
			defaultFilters: {
				orderBy: 'timestamp',
				orderDirection: 'desc',
				search: undefined,
				minDatetime: formatDate(
					new Date(sub(new Date(), { weeks: 1 }))
				),
				maxDatetime: formatDate(new Date()),
				changedByUserId: undefined,
				SourceApplication: ['VueSupport'],
				entityType: [
					'FeatureCustomerLogModel',
					'FeaturesSwitchesModel'
				],
				changedEntityId: this.changedEntityId,
				recordStart: 1,
				maxCount: 10
			},
			tableSource: {
				headers: [],
				displayHeader: true,
				source: {}
			},
			loading: false,
			dataCustomers: [],
			dataDisplay: false,
			defaultUrlNames: [
				{ url: '/features', name: 'Beta Feature Manager' },
				{ name: 'History' }
			],
			searchWidth: 220,
			placeholderHistory: '',
			featureLog: {},
			windowWidth: window.innerWidth,
			previousDayMinDatetime: {
				minDatetime: formatDate(
					new Date(sub(new Date(), { weeks: 1 }))
				),
				maxDatetime: formatDate(new Date())
			}
		};
	},
	created() {
		let featureHistoryHeader = this.changedEntityId
			? headerOptions.filter(h => h.key !== 'description')
			: headerOptions;

		this.tableSource.headers = featureHistoryHeader;
		this.setSearchInput();
	},
	computed: {
		...mapGetters(OPTION_MODULE_NAME, [LOGS]),
		...mapGetters(MODULE_NAME, [BETA_FEATURES, BETA_FEATURES_ALL]),
		...mapState('historyLogs', ['users']),

		resultCounter() {
			return this.tableSource.source.totalCount;
		},
		hasResults() {
			return this.resultCounter > 0;
		},
		handleSort() {
			return {
				currentSort: this.filters.orderBy,
				currentSortDir: this.filters.orderDirection
			};
		},
		pageTitle() {
			return this[LOGS].pageTitle || '';
		},
		urlNames() {
			const routeBreadcrumb = this.$route.meta.breadcrumb;
			const storeBreadcrumb = this.$store.getters.breadcrumbs(
				this.$route.path
			);

			if (!_.isEmpty(routeBreadcrumb)) {
				return routeBreadcrumb;
			}

			if (!_.isEmpty(storeBreadcrumb)) {
				return storeBreadcrumb;
			}

			return this.defaultUrlNames;
		},
		hideFeatureColumn() {
			return !(this.changedEntityId > 0);
		}
	},
	methods: {
		...mapActions(OPTION_MODULE_NAME, [SET_LOGS, SET_USERS_LOGS]),
		...mapActions(MODULE_NAME, [SET_BETA_FEATURES_ALL]),
		...mapActions('historyLogs', ['getUsers']),

		async fetchDataLogs() {
			this.loading = true;

			let filteredQuery = removeObjectEmptyValues(this.filters);
			filteredQuery.minDatetime = parseDateToUTC(
				filteredQuery.minDatetime
			);
			filteredQuery.maxDatetime = parseDateToUTC(
				filteredQuery.maxDatetime,
				true
			);

			try {
				await this[SET_LOGS](filteredQuery);
			} finally {
				this.loading = false;
			}
		},
		getCustomerNames(action, customerId, data) {
			const allCompaniesLabel = ' (All Companies/Sublocations)';
			const index = action === 'Delete' ? 0 : 1;
			const customers = data.items[index]?.Customers ?? [];

			return customers
				.filter(c => c.CustomerId === customerId)
				.flatMap(customer => {
					const showAllCompanies =
						customer.ActiveCompanies.length ===
						customer.TotalCompanies;

					return showAllCompanies
						? `${customer.CustomerNumber} - ${customer.CustomerName}${allCompaniesLabel}`
						: customer.ActiveCompanies.map(
								ac => `(${ac.CompanyNumber}) ${ac.CompanyName}`
						  );
				});
		},
		getActionItems(items, actionType, data) {
			return items
				.filter(item => item.Action === actionType)
				.flatMap(item => {
					const value =
						actionType === 'Add'
							? item.NewValue
							: item.OriginalValue;

					return item.ChangedPropertyName.includes('ActiveCompanies')
						? `(${value.CompanyNumber}) ${value.CompanyName}`
						: this.getCustomerNames(
								actionType,
								value.CustomerId,
								data
						  );
				});
		},
		async loadUsers() {
			try {
				let params = {
					entityType: 'FeaturesSwitchesModel',
					minDatetime: parseDateToUTC(
						this.previousDayMinDatetime.minDatetime
					),
					maxDatetime: parseDateToUTC(
						this.previousDayMinDatetime.maxDatetime,
						true
					)
				};
				if (this.changedEntityId) {
					Object.assign(params, {
						changedEntityId: this.changedEntityId
					});
				}
				await this.getUsers(params);
			} finally {
				this.loading = false;
			}
		},
		setSearchInput() {
			const isEntityChanged = Boolean(this.changedEntityId);

			this.placeholderHistory = isEntityChanged
				? 'Search by client name/ #, company or subLocation'
				: 'Search by feature name';

			this.searchWidth = isEntityChanged ? 380 : 250;
		},
		displayModalClick(index) {
			if (this.tableSource.source?.items?.length > 0) {
				this.featureLog = this.tableSource.source?.items[index];
				this.dataDisplay = true;
			}
		},
		updateRoute(filters) {
			let queries = removePropertiesWithSameValues(
				filters,
				this.defaultFilters
			);

			queries = removeObjectEmptyValues(queries);

			let pathQuery = ['/features', 'history'];
			if (this.changedEntityId) {
				pathQuery.splice(1, 0, this.changedEntityId);
			}
			const path = pathQuery.join('/');

			this.$router
				.push({
					path: path,
					query: queries
				})
				.catch(() => {});
		},
		updateDirection(sortValue) {
			this.filters.orderDirection = sortValue.sortDir;
			this.filters.orderBy = sortValue.sortBy;
		},
		updatedPagination(value) {
			this.filters.recordStart = value.page;
			this.filters.maxCount = value.countPerPage;
		},
		closeFeaturesCustomerModal(value) {
			this.dataDisplay = value;
		}
	},
	watch: {
		'filters.changedEntityId': {
			immediate: true,
			handler(val) {
				if (val) {
					this.filters.recordStart = 1;
				}
			}
		},
		filters: {
			deep: true,
			async handler(newquery) {
				this.updateRoute(newquery);
			}
		},
		'$route.query': {
			immediate: true,
			handler(newQuery) {
				if (newQuery.maxCount) {
					const availablemaxCount = [10, 20, 30, 50, 100];

					if (availablemaxCount.includes(this.maxCount)) {
						newQuery.maxCount = this.maxCount;
					} else {
						newQuery.maxCount = this.defaultFilters.maxCount;
					}
				}

				this.filters = { ...this.defaultFilters, ...newQuery };

				const newMin = parseDateToUTC(this.filters.minDatetime);
				const newMax = parseDateToUTC(this.filters.maxDatetime);

				if (
					this.previousDayMinDatetime.minDatetime !== newMin ||
					this.previousDayMinDatetime.maxDatetime !== newMax
				) {
					this.previousDayMinDatetime = {
						minDatetime: newMin,
						maxDatetime: newMax
					};
				}

				this.fetchDataLogs();
			}
		},
		[LOGS]: {
			deep: true,
			handler(val) {
				if (!_.isEmpty(val)) {
					this.renderLogs();
				}
			}
		},
		previousDayMinDatetime: {
			deep: true,
			immediate: true,
			handler() {
				this.loadUsers();
			}
		}
	}
};
</script>

<style lang="scss">
/**
  * Beta Feature History
*/

/*------------------------------------*\
  Result Filter - BASE
\*------------------------------------*/

.filter {
	display: flex;
	align-items: center;

	&__fields,
	&__summary {
		display: none;
	}

	.modal__body &__fields--modal {
		.beta-feature-history {
			width: 100%;
			display: flex;
			flex-direction: column;
			gap: $gutter-lg;

			.dropdown {
				width: 100%;
				margin: 0;
			}
		}
	}

	&__actions {
		display: flex;
		gap: $gutter-lg;

		.filter__reset-btn {
			margin-left: 0;
			margin-right: 0;
		}

		.btn__verbose {
			display: block;
		}

		// fix layout on mobiles with less than 360px ( Samsung Galaxy S4, Iphone 5, etc )
		@include mq($until: sm-l) {
			.filter__trigger {
				min-width: 44px;
			}
		}

		.filter__trigger .icon {
			margin-right: 0;
		}
	}
}

/*------------------------------------*\
  Result Filter - BASE TABLET-SMALL
\*------------------------------------*/

@include mq($from: sm-l) {
	.filter {
		&__actions {
			margin-left: 0;
			margin-right: $gutter-lg;
		}

		.modal {
			width: auto;
			height: auto;
			max-height: 80vh;
		}
	}
}

@include mq($from: md) {
	.filter {
		.modal {
			&__body {
				.beta-feature-history {
					flex-direction: initial;
					align-items: center;

					.dropdown {
						width: 170px;
						margin-left: $gutter-lg;
					}
				}
			}
		}

		&__actions {
			flex-direction: row-reverse;
			margin-right: 0;
			gap: $gutter-lg;

			.filter__trigger .icon {
				margin-right: $gutter-md;
			}

			.filter__reset-btn {
				order: 1;
			}
		}

		&__summary {
			display: inline;
			font-size: $filter-font-size;
			color: $neutral-2;
			@include ellipsis;
		}

		&__summary-counter {
			font-weight: $bold-font-weight;
			margin-right: $gutter-md;
		}
	}
}

/*------------------------------------*\
  Result Filter - BASE DESKTOP
\*------------------------------------*/

@include mq($from: md-l) {
	.filter {
		gap: $gutter-md;
		flex-direction: row-reverse;

		&__fields {
			display: flex;
		}

		.beta-feature-history {
			display: flex;
			gap: $gutter-md;
		}

		&__summary {
			display: none;
		}

		&__actions {
			.filter__trigger {
				display: none;
			}
		}
	}
}

.beta-history-data {
	&__text-emphasis,
	&__text-action {
		display: flex;
	}
	&__action-description {
		text-transform: none;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
	&__action-detail-button {
		text-transform: none;
		text-align: right;
		font-size: $bd-txt-font-size-sm;
		text-wrap: nowrap;
	}
}

/*------------------------------------*\
  Beta Feature History Table
\*------------------------------------*/

.table-container--beta-features-history {
	.table {
		@include mq($from: md) {
			table-layout: fixed;
		}
	}
	.table__col {
		width: 100%;

		@include mq($from: md) {
			&--date {
				width: 120px;
			}
			&--feature,
			&--action {
				width: auto;
			}
			&--changed-by {
				width: 180px;
			}
		}

		@include mq($from: md-l) {
			&--date {
				width: 200px;
			}
			&--feature {
				width: 291.5px;
			}
			&--action {
				width: auto;
			}
			&--changed-by {
				width: 217px;
			}
		}
	}
}

/*------------------------------------*\
  Beta Feature History Table - Change Date
\*------------------------------------*/
strong {
	@include body-text('regular-emphasis');
	text-transform: capitalize;
	font-size: $bd-txt-font-size-regular;
}
.beta-history-data {
	&__action-description,
	&__text-emphasis {
		strong {
			font-weight: 600;
		}
	}
}
</style>

<style lang="scss" scoped>
.results-toolbar {
	width: 100%;
}
::v-deep .results-toolbar__search {
	@include mq($from: md) {
		width: 220px;
	}
}
</style>
